import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import Logo from "@/components/Logo";
import { dashboardPath, useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(identifier, password);
      const from = location.state?.from;
      // Only allow internal (same-app) return URLs; preserve the full path + query
      // (e.g. /member/live-learn?topic=…&msg=…) so a push deep-link survives login.
      const internal = typeof from === "string" && from.startsWith("/") && !from.startsWith("//") && from !== "/login";
      navigate(internal ? from : dashboardPath(user.role), { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050506] flex items-center justify-center px-4 py-12 relative overflow-hidden" data-testid="login-page">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[400px] w-[700px] bg-[#D4AF37]/[0.05] blur-[120px] rounded-full pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-md"
      >
        <div className="flex justify-center mb-8">
          <Logo />
        </div>
        <div className="card-lux p-8 sm:p-10">
          <p className="eyebrow-label mb-3 text-center">Member Access</p>
          <h1 className="font-heading text-2xl font-bold text-white text-center mb-8">
            Welcome Back
          </h1>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                Username or Email
              </label>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="input-lux"
                placeholder="e.g. 881208065608 or you@email.com"
                required
                autoComplete="username"
                data-testid="login-identifier-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium text-[#94A3B8] tracking-wide">
                  Password
                </label>
                <Link to="/forgot-password" className="text-[11px] text-[#D4AF37] hover:text-[#E5C158] transition-colors" data-testid="forgot-password-link">
                  Forgot Password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-lux pr-11"
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-[#94A3B8] hover:text-white transition-colors"
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && (
              <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="login-error-message">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              className="btn-gold w-full py-3 text-sm disabled:opacity-60"
              data-testid="login-submit-button"
            >
              {loading ? "Authenticating..." : "Login"}
            </button>
          </form>
          <div className="gold-divider my-7" />
          <p className="text-sm text-[#64748B] text-center">
            New to PLUSMAKER?{" "}
            <Link to="/register" className="text-[#D4AF37] hover:text-[#E5C158] font-medium" data-testid="login-register-link">
              Register with a referral code
            </Link>
          </p>
        </div>
        <p className="text-center mt-6">
          <Link to="/" className="text-xs text-[#64748B] hover:text-white transition-colors" data-testid="login-back-home-link">
            ← Back to website
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
